import {
  PlatformAdmin,
  PlatformAdminCreatePayload,
  PlatformAdminUpdatePayload,
  PlatformAuthResponse,
  Tenant,
  TenantAdminCreatePayload,
  TenantCreatePayload,
  TenantUpdatePayload,
  TenantUser,
  TenantUserUpdatePayload,
} from './types'

const getApiBase = (): string => {
  const envUrl = import.meta.env.VITE_API_BASE_URL
  if (import.meta.env.PROD) {
    if (envUrl && !envUrl.includes('localhost')) {
      return envUrl.replace(/\/+$/, '')
    }
    return 'https://api.parel.com.tr'
  }
  return (envUrl || 'http://localhost:8000').replace(/\/+$/, '')
}

const API_BASE = getApiBase()
const BASE_PATH = `${API_BASE}/api/v1`

let adminToken: string | null = null

export function setAdminToken(token: string | null) {
  adminToken = token
  if (token) {
    localStorage.setItem('cost_admin_token', token)
  } else {
    localStorage.removeItem('cost_admin_token')
  }
}

export function getAdminToken(): string | null {
  if (!adminToken) {
    adminToken = localStorage.getItem('cost_admin_token')
  }
  return adminToken
}

export function clearAdminSession() {
  adminToken = null
  localStorage.removeItem('cost_admin_token')
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  const token = getAdminToken()
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${BASE_PATH}${endpoint}`, {
    ...options,
    headers,
  })

  if (!res.ok) {
    if (res.status === 401) {
      clearAdminSession()
    }
    let errorDetail = `İstek başarısız: ${res.status}`
    try {
      const body = await res.json()
      if (body.detail) errorDetail = body.detail
    } catch {
      // ignore JSON parse error
    }
    throw new Error(errorDetail)
  }

  if (res.status === 204) {
    return {} as T
  }

  return res.json()
}

export const api = {
  // Authentication
  login: (data: { email: string; password: string }) =>
    request<PlatformAuthResponse>('/platform/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getMe: () => request<PlatformAdmin>('/platform/auth/me'),

  logout: () => {
    clearAdminSession()
  },

  // Health / System
  getHealth: () => request<{ status: string }>('/health'),

  // Tenants Management
  listTenants: () => request<Tenant[]>('/platform/tenants'),

  getTenant: (id: string) => request<Tenant>(`/platform/tenants/${id}`),

  createTenant: (data: TenantCreatePayload) =>
    request<Tenant>('/platform/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTenant: (id: string, data: TenantUpdatePayload) =>
    request<Tenant>(`/platform/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  activateTenant: (id: string) =>
    request<Tenant>(`/platform/tenants/${id}/activate`, {
      method: 'POST',
    }),

  deactivateTenant: (id: string) =>
    request<Tenant>(`/platform/tenants/${id}/deactivate`, {
      method: 'POST',
    }),

  // Tenant Admins Management
  listTenantAdmins: (tenantId: string) =>
    request<TenantUser[]>(`/platform/tenants/${tenantId}/admins`),

  listTenantUsers: (tenantId: string) =>
    request<TenantUser[]>(`/tenants/${tenantId}/users`),

  createTenantAdmin: (tenantId: string, data: TenantAdminCreatePayload) =>
    request<TenantUser>(`/platform/tenants/${tenantId}/admins`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTenantUser: (tenantId: string, userId: string, data: TenantUserUpdatePayload) =>
    request<TenantUser>(`/tenants/${tenantId}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteTenantUser: (tenantId: string, userId: string) =>
    request<void>(`/tenants/${tenantId}/users/${userId}`, {
      method: 'DELETE',
    }),

  // Platform Admins Management
  listPlatformAdmins: () => request<PlatformAdmin[]>('/platform/admins'),

  createPlatformAdmin: (data: PlatformAdminCreatePayload) =>
    request<PlatformAdmin>('/platform/admins', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updatePlatformAdmin: (id: string, data: PlatformAdminUpdatePayload) =>
    request<PlatformAdmin>(`/platform/admins/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
}
